import { getIsFromNodeEventSelector, getNodeDataByNodeId } from '@common-reducers/ChatbotSelectors';
import { focusNodeEventsThunk } from '@common-reducers/ChatbotThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { TriggerSource, TriggerType, getAvailableTriggerSources, UserRole } from '@common/types/trigger.types';
import { MoreVert, Settings } from "@mui/icons-material";
import { Box, Chip, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import { Stack } from '@mui/system';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import BTChatbotModal from '../../bot-components/bt-chatbot-modal';
import NodeMenu from '../../bot-components/node-menu';
import { nodeTypes } from '../bt-nodes-def';
import classes from "./trigger-node.module.scss";
import { selectUserObject } from '@/common/reducers/UserSelectors';

interface TriggerState {
    type: 'trigger';
    triggerType: TriggerType;
    contacts: string[];
    allowedTriggerSources?: TriggerSource[];
}

interface Props {
    id: string;
}

export default function TriggerNodeContainer({ id }: Props) {
    const dispatch = useAppDispatch();
    const { sessionId } = useParams<{ sessionId: string }>();
    const isFromNodeEvent = useAppSelector(state => getIsFromNodeEventSelector(state, id, sessionId));
    const isReadOnly = useAppSelector(state => state.ChatbotReducer.isReadOnly);
    const data = useAppSelector(state => getNodeDataByNodeId(state, id)) as TriggerState;

    // Get user role directly from the state slice
    // Adjust 'UserReducer' and 'role' if your state structure is different
    const userRole = useAppSelector(state => selectUserObject(state).role);
    const availableTriggerSources = getAvailableTriggerSources(userRole);

    const [menuOpen, setMenuOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const nodeType = nodeTypes?.[data?.type]

    const handleOpenNodeMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuOpen((prev => !prev))
        setAnchorEl(event.currentTarget);
    };

    const handleCloseNodeMenu = () => {
        setMenuOpen(false);
        setAnchorEl(null);
    }

    const handleOpenModal = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleNodeClick = () => {
        if (isReadOnly && isFromNodeEvent) {
            dispatch(focusNodeEventsThunk(id));
        }
    };

    const getTriggerSummary = (data: TriggerState): { mainText: string; subText: string } => {
        const contactCount = data?.contacts?.length || 0;
        return {
            mainText: data?.triggerType === 'allowList' ? 'Allow only:' : 'Allow all except:',
            subText: `${contactCount} contact${contactCount !== 1 ? 's' : ''}`
        };
    };

    // Get the trigger sources information based on *current data* and *available sources*
    const getTriggerSourcesInfo = (data: TriggerState) => {
        const normalizedSources = data?.allowedTriggerSources || [];
        
        // Create a map of which sources are enabled *in the current configuration*
        // ONLY consider sources available to this user role
        const sourceMap = availableTriggerSources.reduce((acc, source) => {
            acc[source.value] = normalizedSources.includes(source.value);
            return acc;
        }, {} as Record<TriggerSource, boolean>);
        
        return sourceMap;
    };

    const borderColor = !isFromNodeEvent && isReadOnly ? '#939393' : nodeType?.color;
    const sourceMap = getTriggerSourcesInfo(data);

    const content = (
        <Stack className={classes.trigger_container}
            sx={{
                border: `6px solid ${borderColor}`,
                cursor: (!isFromNodeEvent && isReadOnly) ? 'default' : 'pointer',
                minWidth: '220px',
                minHeight: '180px',
                padding: '12px',

                '&:hover': {
                    borderColor: `darken(${borderColor}, 10%)`,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                }
            }}
            onClick={handleNodeClick}
        >
            {/* Header Area with title and buttons */}
            <Box sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1
            }}>
                <div className={classes.trigger_title}>Trigger</div>

                <Box sx={{
                    display: 'flex',
                    gap: '4px'
                }}>
                    {!isReadOnly && (
                        <Tooltip title="Edit trigger" arrow placement="top">
                            <IconButton
                                size="small"
                                sx={{ color: '#666666' }}
                                onClick={handleOpenModal}
                                className={classes.settings_button}
                            >
                                <Settings fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton
                        size="small"
                        sx={{ color: '#666666' }}
                        onClick={handleOpenNodeMenu}
                        disabled={isReadOnly}
                    >
                        <MoreVert fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <NodeMenu
                type={data?.type}
                open={menuOpen}
                onClose={handleCloseNodeMenu}
                anchorEl={anchorEl}
                id={id}
            />

            <Stack direction="row" justifyContent="start" alignItems="center" >
                <div className={classes.trigger_description}>This node triggers the flow</div>
            </Stack>

            {/* Trigger Source Chips */}
            <Stack sx={{ my: 1, px: 1, alignItems: 'flex-start' }}>
                <Typography variant="caption" sx={{ color: '#666666', fontWeight: 'bold', mb: 1 }}>
                    Sources:
                </Typography>
                <Stack spacing={1.5} direction="column" sx={{ width: '100%' }}>
                    {availableTriggerSources.map(source => {
                        const isActive = sourceMap[source.value];
                        const IconComponent = source.icon;
                        
                        return (
                            <Tooltip key={source.value} title={`${source.label}${source.description ? ` - ${source.description}` : ''}`} arrow>
                                <Chip
                                    icon={<IconComponent fontSize="small" />}
                                    label={source.label}
                                    size="small"
                                    color={isActive ? "primary" : "default"}
                                    variant={isActive ? "filled" : "outlined"}
                                    sx={{ opacity: isActive ? 1 : 0.5 }}
                                />
                            </Tooltip>
                        );
                    })}
                    {/* Show message when no sources are selected *from the available ones* */}
                    {availableTriggerSources.length > 0 && Object.values(sourceMap).every(value => !value) && (
                        <Typography variant="caption" sx={{ color: '#f44336', fontStyle: 'italic', mt: 0.5 }}>
                            No trigger sources selected. Click the settings icon to configure.
                        </Typography>
                    )}
                    {/* Show message when no sources are available for the role */}
                    {availableTriggerSources.length === 0 && (
                        <Typography variant="caption" sx={{ color: '#666666', fontStyle: 'italic', mt: 0.5 }}>
                             No trigger sources available for your role.
                        </Typography>
                    )}
                </Stack>
            </Stack>

            <Stack spacing={0.5} sx={{ mt: 'auto', px: 1, alignItems: 'flex-start' }}>
                <Typography variant="caption" sx={{ color: '#666666', fontWeight: 'bold' }}>
                    Contact rules:
                </Typography>
                <Typography variant="caption" sx={{ color: '#666666' }}>
                    {getTriggerSummary(data).mainText}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666666' }}>
                    {getTriggerSummary(data).subText}
                </Typography>
            </Stack>

            {/* Edit Modal */}
            <Modal
                id={modalOpen ? 'trigger-edit-modal' : undefined}
                open={modalOpen}
                onClose={handleCloseModal}
                sx={{
                    margin: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <BTChatbotModal type="setTrigger" onClose={handleCloseModal} id={id} />
            </Modal>
        </Stack>
    );

    if (isReadOnly && isFromNodeEvent) {
        return (
            <Tooltip title="Click to view related events in the timeline" arrow placement="top">
                {content}
            </Tooltip>
        );
    }

    return content;
} 